import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Coins, Zap, Check, Sparkles, Wallet, Receipt, X, ListChecks } from "lucide-react";
import Btn from "../components/Btn";
import TnCModal from "../components/TnCModal";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PromoCountdown } from "../components/PromoCountdown";

const fmt = (n) => "Rp" + Number(n).toLocaleString("id-ID");

const perLabel = (p) => {
  const d = p.grant?.vip_days || 0;
  if (d >= 360) return "/tahun";
  if (d === 30) return "/bulan";
  if (d) return `/${Math.round(d / 30)} bln`;
  return "";
};

const PAY_METHODS = [
  { id: "doku", label: "Kartu · VA · e-Wallet · QRIS · Retail", icon: Wallet },
];

export default function Pricing() {
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [showTnc, setShowTnc] = useState(false);
  const [method, setMethod] = useState("doku");
  const [selected, setSelected] = useState(null);
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const openCheckout = (p) => { setAgreed(false); setMethod("doku"); setSelected(p); };

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.products)).catch(() => {});
  }, []);

  const checkout = async (product_id) => {
    if (!agreed) { toast.error("Setujui Syarat & Ketentuan terlebih dahulu"); return; }
    setBusy(product_id);
    try {
      const { data } = await api.post("/payments/doku/create", { product_id, origin_url: window.location.origin, accepted_tnc: true });
      if (!data.payment_url) { toast.error("Gagal membuat pembayaran. Coba lagi."); setBusy(null); return; }
      window.location.href = data.payment_url;
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(null);
    }
  };

  const subs = products.filter((p) => p.type === "subscription");
  const tokens = products.filter((p) => p.type === "tokens");
  const packs = products.filter((p) => p.type === "tryout_pack");

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="font-heading font-black text-3xl sm:text-4xl">Upgrade Kemampuanmu</h1>
        <p className="text-su-muted font-medium mt-1">Buka semua fitur premium dan raih skor tertinggi.</p>
        {user?.vip && <span className="inline-flex items-center gap-1.5 mt-3 bg-su-secondary/20 text-su-secondaryShadow font-heading font-black px-4 py-1.5 rounded-full"><Crown className="w-4 h-4 fill-current" /> Kamu VIP aktif</span>}
      </div>

      <TnCModal open={showTnc} onClose={() => setShowTnc(false)} />

      {/* tryout packs */}
      {packs.length > 0 && (
        <section>
          <h2 className="font-heading font-black text-xl mb-1 flex items-center gap-2"><ListChecks className="w-6 h-6 text-su-primary" /> Paket Tryout</h2>
          <p className="text-su-muted font-medium text-sm mb-4">Kerjakan sejumlah tryout tanpa token/energi. Tiap tryout punya papan peringkat sendiri.</p>
          <div className="grid sm:grid-cols-3 gap-5">
            {packs.map((p) => (
              <div key={p.product_id} className={`card-chunky p-6 flex flex-col relative ${p.discount_label ? "ring-4 ring-su-secondary/30" : "ring-4 ring-su-primary/30"}`}>
                {p.discount_label
                  ? <span className="absolute -top-3 left-6 bg-su-secondary text-black text-xs font-heading font-black px-3 py-1 rounded-full">{p.discount_label}</span>
                  : <span className="absolute -top-3 left-6 bg-su-primary text-white text-xs font-heading font-black px-3 py-1 rounded-full">TANPA TOKEN</span>}
                <div className="w-14 h-14 rounded-2xl bg-su-primary/15 grid place-items-center mb-3"><ListChecks className="w-7 h-7 text-su-primary" /></div>
                <h3 className="font-heading font-black text-lg">{p.name}</h3>
                <p className="font-heading font-black text-2xl my-2">{p.original_amount > p.amount && <span className="text-base font-bold text-su-muted line-through mr-2">{fmt(p.original_amount)}</span>}{fmt(p.amount)}</p>
                {p.original_amount > p.amount && p.promo_ends_at && <div className="mb-2"><PromoCountdown endsAt={p.promo_ends_at} testId={`promo-countdown-${p.product_id}`} /></div>}
                <ul className="text-xs font-medium text-su-muted space-y-1 mb-4 flex-1">
                  {(p.features || []).map((x) => <li key={x} className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-su-success shrink-0 mt-0.5" />{x}</li>)}
                </ul>
                <Btn className="w-full" data-testid={`buy-${p.product_id}`} onClick={() => openCheckout(p)}>Beli</Btn>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* VIP subscriptions */}
      <section>
        <h2 className="font-heading font-black text-xl mb-4 flex items-center gap-2"><Crown className="w-6 h-6 text-su-secondary" /> Langganan VIP</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {subs.map((p) => (
            <div key={p.product_id} className="card-chunky p-6 relative ring-4 ring-su-secondary/30">
              {p.discount_label
                ? <span className="absolute -top-3 left-6 bg-su-secondary text-black text-xs font-heading font-black px-3 py-1 rounded-full">{p.discount_label}</span>
                : <span className="absolute -top-3 left-6 bg-su-primary text-white text-xs font-heading font-black px-3 py-1 rounded-full">TERPOPULER</span>}
              <h3 className="font-heading font-black text-lg">{p.name}</h3>
              <p className="font-heading font-black text-3xl my-2">{p.original_amount > p.amount && <span className="text-lg font-bold text-su-muted line-through mr-2">{fmt(p.original_amount)}</span>}{fmt(p.amount)}<span className="text-su-muted font-bold text-sm">{perLabel(p)}</span></p>
              {p.original_amount > p.amount && p.promo_ends_at && <PromoCountdown endsAt={p.promo_ends_at} testId={`promo-countdown-${p.product_id}`} />}
              <ul className="space-y-2 text-sm font-medium text-su-muted my-4">
                {(p.features?.length ? p.features : ["Energi penuh tiap hari", "Semua tryout & CBT", "AI Mentor prioritas", "Badge & bingkai VIP"]).map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="w-4 h-4 text-su-success" /> {x}</li>
                ))}
              </ul>
              <Btn variant="secondary" className="w-full" data-testid={`buy-${p.product_id}`} onClick={() => openCheckout(p)}>
                Jadi VIP
              </Btn>
            </div>
          ))}
        </div>
      </section>

      {/* token packs */}
      <section>
        <h2 className="font-heading font-black text-xl mb-4 flex items-center gap-2"><Coins className="w-6 h-6 text-su-secondaryShadow" /> Paket Token</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {tokens.map((p) => (
            <div key={p.product_id} className={`card-chunky p-6 text-center relative ${p.discount_label ? "ring-4 ring-su-secondary/30" : ""}`}>
              {p.discount_label && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-su-secondary text-black text-xs font-heading font-black px-3 py-1 rounded-full">{p.discount_label}</span>}
              <div className="w-14 h-14 rounded-2xl bg-su-secondary/20 grid place-items-center mx-auto mb-3"><Coins className="w-7 h-7 text-su-secondaryShadow" /></div>
              <h3 className="font-heading font-black text-lg">{p.name}</h3>
              <p className="font-heading font-black text-2xl my-2">{p.original_amount > p.amount && <span className="text-base font-bold text-su-muted line-through mr-2">{fmt(p.original_amount)}</span>}{fmt(p.amount)}</p>
              {p.original_amount > p.amount && p.promo_ends_at && <div className="mb-2 flex justify-center"><PromoCountdown endsAt={p.promo_ends_at} testId={`promo-countdown-${p.product_id}`} /></div>}
              <p className="text-xs text-su-muted font-bold flex items-center gap-1 justify-center mb-4"><Zap className="w-3.5 h-3.5 text-su-energy" /> + bonus energi</p>
              <Btn className="w-full" data-testid={`buy-${p.product_id}`} onClick={() => openCheckout(p)}>
                Beli
              </Btn>
            </div>
          ))}
        </div>
      </section>

      <p className="text-center text-xs text-su-muted font-bold flex items-center gap-1.5 justify-center">
        <Sparkles className="w-3.5 h-3.5" /> Pembayaran aman via Midtrans (QRIS, GoPay, VA, Alfamart)
      </p>
      <p className="text-center">
        <Link to="/transactions" data-testid="view-transactions-link" className="text-sm text-su-primary font-heading font-extrabold hover:underline inline-flex items-center gap-1.5">
          <Receipt className="w-4 h-4" /> Lihat Riwayat Transaksi
        </Link>
      </p>

      {/* checkout modal: method + T&C AFTER clicking buy */}
      {selected && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4" data-testid="checkout-modal" onClick={() => busy ? null : setSelected(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-md card-chunky p-6 animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-black text-lg">Selesaikan Pembayaran</h2>
              <button data-testid="checkout-close" onClick={() => setSelected(null)} className="w-9 h-9 rounded-lg grid place-items-center border border-su-border hover:border-su-primary"><X className="w-5 h-5" /></button>
            </div>

            <div className="rounded-2xl bg-su-bg border-2 border-su-border p-4 mb-4 flex items-center justify-between">
              <div>
                <p className="font-heading font-black">{selected.name}</p>
                <p className="text-xs text-su-muted font-medium">{selected.type === "subscription" ? "Langganan VIP" : "Paket Token"}</p>
              </div>
              <span className="font-heading font-black text-xl text-su-primary">{fmt(selected.amount)}</span>
            </div>

            <p className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><Wallet className="w-4 h-4 text-su-primary" /> Metode Pembayaran</p>
            <div className="grid gap-2 mb-4">
              {PAY_METHODS.map((m) => (
                <button key={m.id} data-testid={`method-${m.id}`} onClick={() => setMethod(m.id)}
                  className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-colors ${method === m.id ? "border-su-primary bg-su-primary/10" : "border-su-border hover:border-su-primary/50"}`}>
                  <m.icon className={`w-5 h-5 shrink-0 ${method === m.id ? "text-su-primary" : "text-su-muted"}`} />
                  <span className="font-heading font-bold text-sm">{m.label}</span>
                </button>
              ))}
            </div>

            <div className="flex items-start gap-3 mb-4" data-testid="tnc-box">
              <input id="tnc" type="checkbox" data-testid="tnc-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-[var(--su-primary)] shrink-0" />
              <label htmlFor="tnc" className="text-xs text-su-muted font-medium">
                Saya telah membaca dan menyetujui{" "}
                <button type="button" data-testid="tnc-open" onClick={(e) => { e.preventDefault(); setShowTnc(true); }} className="text-su-primary font-bold underline">Syarat & Ketentuan</button>
                {" "}SkorUp.io (pembayaran digital bersifat final; lihat kebijakan refund).
              </label>
            </div>

            <Btn className="w-full" data-testid="checkout-pay" disabled={busy === selected.product_id || !agreed} onClick={() => checkout(selected.product_id)}>
              {busy === selected.product_id ? "Mengalihkan..." : `Bayar ${fmt(selected.amount)}`}
            </Btn>
            <p className="text-[11px] text-su-muted mt-3 text-center">💳 Diproses aman via <b>DOKU</b>. Kamu akan diarahkan ke halaman pembayaran DOKU (kartu, VA, e-wallet, QRIS & retail).</p>
          </div>
        </div>
      )}
    </div>
  );
}
